// WhatsApp Business API Integration for Chilean Emergency Response System
// This service handles WhatsApp notifications for the healthcare emergency system

interface WhatsAppMessage {
  to: string; // Phone number in international format (+56...)
  type: 'text' | 'template' | 'location' | 'media';
  content: string;
  template?: string;
  metadata?: {
    emergencyId?: string;
    patientName?: string;
    severity?: string;
    timestamp?: string;
  };
}

interface EmergencyNotification {
  patientName: string;
  emergencyId: string;
  severity: 'emergency' | 'urgent' | 'moderate';
  symptoms: string[];
  location?: string;
  ambulanceId?: string;
  estimatedArrival?: string;
}

interface TelemedicineInvite {
  patientName: string;
  doctorName: string;
  consultationLink: string;
  estimatedWaitTime: string;
}

class WhatsAppIntegrationService {
  private baseUrl = 'https://graph.facebook.com/v17.0'; // WhatsApp Business API
  private phoneNumberId = import.meta.env.VITE_WHATSAPP_PHONE_NUMBER_ID || 'YOUR_PHONE_NUMBER_ID';
  private accessToken = import.meta.env.VITE_WHATSAPP_ACCESS_TOKEN || 'YOUR_ACCESS_TOKEN';

  // Chilean emergency service numbers
  private emergencyContacts = {
    ambulance: '+56131', // Chilean ambulance service
    police: '+56133',    // Chilean police
    fire: '+56132'       // Chilean fire department
  };

  private formatChileanPhoneNumber(phone: string): string {
    // Convert Chilean phone numbers to international format
    let cleanPhone = phone.replace(/\D/g, '');
    
    // If it starts with 56, it's already international
    if (cleanPhone.startsWith('56')) {
      return `+${cleanPhone}`;
    }
    
    // If it's a Chilean mobile number (9xxxxxxxx)
    if (cleanPhone.startsWith('9') && cleanPhone.length === 9) {
      return `+569${cleanPhone.substring(1)}`;
    }
    
    // If it's missing country code, add +56
    if (cleanPhone.length === 8 || cleanPhone.length === 9) {
      return `+56${cleanPhone}`;
    }
    
    return `+${cleanPhone}`;
  }

  private async sendWhatsAppMessage(message: WhatsAppMessage): Promise<boolean> {
    try {
      // In development, we'll simulate the WhatsApp API call
      if (import.meta.env.DEV) {
        console.log('📱 WhatsApp Message (Simulated):', {
          to: message.to,
          content: message.content,
          metadata: message.metadata
        });
        return true;
      }

      // Real WhatsApp Business API call (for production)
      const response = await fetch(`${this.baseUrl}/${this.phoneNumberId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messaging_product: 'whatsapp',
          to: message.to,
          type: message.type,
          text: {
            body: message.content
          }
        })
      });

      return response.ok;
    } catch (error) {
      console.error('WhatsApp API Error:', error);
      return false;
    }
  }

  // Send emergency alert to family members
  async sendEmergencyAlert(
    familyPhones: string[], 
    notification: EmergencyNotification
  ): Promise<void> {
    const severityEmoji = {
      emergency: '🚨',
      urgent: '⚠️',
      moderate: 'ℹ️'
    };

    const message = `${severityEmoji[notification.severity]} *ALERTA MÉDICA*

👤 *Paciente:* ${notification.patientName}
🆔 *Emergencia:* ${notification.emergencyId}
📍 *Ubicación:* ${notification.location || 'No especificada'}

💊 *Síntomas reportados:*
${notification.symptoms.map(s => `• ${s}`).join('\n')}

${notification.ambulanceId ? `🚑 *Ambulancia ${notification.ambulanceId}* despachada` : ''}
${notification.estimatedArrival ? `⏰ *Tiempo estimado:* ${notification.estimatedArrival}` : ''}

Mantente conectado para recibir actualizaciones en tiempo real.

_Sistema de Emergencias IAM-ACV Chile_ 🇨🇱`;

    for (const phone of familyPhones) {
      const formattedPhone = this.formatChileanPhoneNumber(phone);
      await this.sendWhatsAppMessage({
        to: formattedPhone,
        type: 'text',
        content: message,
        metadata: {
          emergencyId: notification.emergencyId,
          patientName: notification.patientName,
          severity: notification.severity,
          timestamp: new Date().toISOString()
        }
      });
    }
  }

  // Send ambulance status updates
  async sendAmbulanceUpdate(
    familyPhones: string[], 
    patientName: string,
    ambulanceId: string,
    status: string,
    location: string,
    eta: string
  ): Promise<void> {
    const statusMessages = {
      'dispatched': '🚑 Ambulancia despachada',
      'en_route_to_patient': '🚗 En camino hacia el paciente',
      'with_patient': '🩺 Paramédicos atendiendo al paciente',
      'en_route_to_hospital': '🏥 Transportando al hospital',
      'arrived_hospital': '✅ Llegó al hospital'
    };

    const message = `🚑 *Actualización Ambulancia ${ambulanceId}*

👤 *Paciente:* ${patientName}
📍 *Estado:* ${statusMessages[status as keyof typeof statusMessages] || status}
🗺️ *Ubicación actual:* ${location}
⏰ *ETA:* ${eta}

Seguimiento en tiempo real disponible en la app.

_Sistema IAM-ACV Chile_ 🇨🇱`;

    for (const phone of familyPhones) {
      const formattedPhone = this.formatChileanPhoneNumber(phone);
      await this.sendWhatsAppMessage({
        to: formattedPhone,
        type: 'text',
        content: message,
        metadata: {
          patientName,
          ambulanceId,
          timestamp: new Date().toISOString()
        }
      });
    }
  }

  // Send telemedicine consultation invite
  async sendTelemedicineInvite(
    patientPhone: string,
    invite: TelemedicineInvite
  ): Promise<void> {
    const message = `📹 *Consulta Médica Programada*

👤 *Paciente:* ${invite.patientName}
👨‍⚕️ *Doctor:* ${invite.doctorName}
⏰ *Tiempo de espera:* ${invite.estimatedWaitTime}

🔗 *Enlace de consulta:*
${invite.consultationLink}

*Instrucciones:*
1️⃣ Haz clic en el enlace
2️⃣ Permite acceso a cámara y micrófono
3️⃣ Espera a que el doctor se conecte

📱 Si tienes problemas técnicos, llama al +56 2 1234 5678

_Telemedicina IAM-ACV Chile_ 🇨🇱`;

    const formattedPhone = this.formatChileanPhoneNumber(patientPhone);
    await this.sendWhatsAppMessage({
      to: formattedPhone,
      type: 'text',
      content: message
    });
  }

  // Send doctor emergency activation confirmation
  async sendDoctorEmergencyConfirmation(
    familyPhones: string[],
    patientName: string,
    doctorName: string,
    diagnosis: string,
    instructions: string
  ): Promise<void> {
    const message = `👨‍⚕️ *Confirmación Médica de Emergencia*

👤 *Paciente:* ${patientName}
🩺 *Doctor:* ${doctorName}
📋 *Diagnóstico:* ${diagnosis}

🚨 *El médico ha confirmado que se requiere atención de emergencia inmediata.*

📝 *Instrucciones médicas:*
${instructions}

🚑 Protocolo de emergencia activado:
• Ambulancia despachada
• Hospital notificado  
• Equipo médico preparándose

Mantente cerca del teléfono para actualizaciones.

_Sistema IAM-ACV Chile_ 🇨🇱`;

    for (const phone of familyPhones) {
      const formattedPhone = this.formatChileanPhoneNumber(phone);
      await this.sendWhatsAppMessage({
        to: formattedPhone,
        type: 'text',
        content: message,
        metadata: {
          patientName,
          doctorName,
          timestamp: new Date().toISOString()
        }
      });
    }
  }

  // Send hospital arrival notification
  async sendHospitalArrivalNotification(
    familyPhones: string[],
    patientName: string,
    hospitalName: string,
    roomNumber: string,
    doctorName: string
  ): Promise<void> {
    const message = `🏥 *Llegada al Hospital*

👤 *Paciente:* ${patientName}
🏥 *Hospital:* ${hospitalName}
🚪 *Sala:* ${roomNumber}
👨‍⚕️ *Doctor a cargo:* ${doctorName}

✅ *El paciente ha llegado al hospital y está siendo atendido por el equipo médico.*

📞 *Información de contacto del hospital:*
• Teléfono: +56 41 234 5678
• Dirección: [Dirección del hospital]

Puedes dirigirte al hospital o llamar para más información.

_Sistema IAM-ACV Chile_ 🇨🇱`;

    for (const phone of familyPhones) {
      const formattedPhone = this.formatChileanPhoneNumber(phone);
      await this.sendWhatsAppMessage({
        to: formattedPhone,
        type: 'text',
        content: message,
        metadata: {
          patientName,
          hospitalName,
          timestamp: new Date().toISOString()
        }
      });
    }
  }

  // Send medication reminder
  async sendMedicationReminder(
    patientPhone: string,
    patientName: string,
    medicationName: string,
    dosage: string,
    time: string
  ): Promise<void> {
    const message = `💊 *Recordatorio de Medicamento*

👤 *${patientName}*
⏰ *Hora:* ${time}

💊 *Medicamento:* ${medicationName}
📏 *Dosis:* ${dosage}

¡No olvides tomar tu medicamento!

Si ya lo tomaste, puedes ignorar este mensaje.
Si tienes dudas, consulta con tu médico.

_Sistema IAM-ACV Chile_ 🇨🇱`;

    const formattedPhone = this.formatChileanPhoneNumber(patientPhone);
    await this.sendWhatsAppMessage({
      to: formattedPhone,
      type: 'text',
      content: message,
      metadata: {
        patientName,
        medicationName,
        timestamp: new Date().toISOString()
      }
    });
  }

  // Send appointment reminder
  async sendAppointmentReminder(
    patientPhone: string,
    patientName: string,
    appointmentDate: string,
    doctorName: string,
    location: string
  ): Promise<void> {
    const message = `📅 *Recordatorio de Cita Médica*

👤 *Paciente:* ${patientName}
📅 *Fecha y hora:* ${appointmentDate}
👨‍⚕️ *Doctor:* ${doctorName}
📍 *Lugar:* ${location}

🔔 *Tu cita médica es mañana*

📋 *Recuerda traer:*
• Carnet de identidad
• Medicamentos actuales
• Exámenes recientes

Si no puedes asistir, llama para reagendar: +56 2 1234 5678

_Sistema IAM-ACV Chile_ 🇨🇱`;

    const formattedPhone = this.formatChileanPhoneNumber(patientPhone);
    await this.sendWhatsAppMessage({
      to: formattedPhone,
      type: 'text',
      content: message,
      metadata: {
        patientName,
        appointmentDate,
        timestamp: new Date().toISOString()
      }
    });
  }

  // Send general health alert (weather, pollution, etc.)
  async sendHealthAlert(
    phones: string[],
    alertType: 'weather' | 'pollution' | 'epidemic',
    title: string,
    message: string,
    recommendations: string[]
  ): Promise<void> {
    const alertEmojis = {
      weather: '🌡️',
      pollution: '💨',
      epidemic: '🦠'
    };

    const whatsappMessage = `${alertEmojis[alertType]} *${title}*

${message}

📋 *Recomendaciones:*
${recommendations.map(r => `• ${r}`).join('\n')}

Si tienes síntomas relacionados, usa el evaluador de síntomas en la app o consulta con tu médico.

_Alerta de Salud IAM-ACV Chile_ 🇨🇱`;

    for (const phone of phones) {
      const formattedPhone = this.formatChileanPhoneNumber(phone);
      await this.sendWhatsAppMessage({
        to: formattedPhone,
        type: 'text',
        content: whatsappMessage,
        metadata: {
          alertType,
          title,
          timestamp: new Date().toISOString()
        }
      });
    }
  }
}

// Export singleton instance
export const whatsappService = new WhatsAppIntegrationService();
export default whatsappService;